import { useEffect, useLayoutEffect, useRef } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';
import { prefersReducedMotion } from '@/lib/motion';
import { entryKey, keepsScroll, pageSignature, persistPositions, savedPosition, savePosition, setRestoring, stampEntry, urlKey, wasStamped } from '@/lib/scroll-memory';

// The window's scroll across navigations (owner report 2026-10-02):
//   * a link or redirect to another page starts it at the top, unless it is
//     the same page in another form (KEEP_SCROLL: the editor's language);
//   * a link to the page already on screen (no #anchor) goes back to its top;
//   * Back / Forward, and a reload (a phone reloading a tab it put away too),
//     return to where that entry was left, unless the page is in another
//     layout than then (its signature: the landing's compass stop);
//   * same-page changes (the landing's ?tool= stops and #anchors) and a pop
//     that stays on the same entry (ChopBox leaving full screen pops the entry
//     it pushed) are left to the page, which scrolls those itself.
// The browser's own restoration is off ('manual'): it fired before the
// returning page had grown and raced the landing page's scroll-to-top.

// Set as this module loads, before any page code: GSAP's ScrollTrigger
// records the value it finds when it registers and writes it back on every
// refresh, so a later 'manual' was undone on the landing page (2026-10-03).
// The entry gets its own key here too, before the router reads it
// (scroll-memory entryKey).
if (typeof window !== 'undefined') {
  try {
    if ('scrollRestoration' in window.history) window.history.scrollRestoration = 'manual';
  } catch { /* a sandboxed frame: the browser keeps its own */ }
  stampEntry();
}

// A restore keeps lining up while the page fills in after the first paint
// (the landing's pins, media and lazy sections; a full page load waits for
// the sign-in check before a page is drawn at all), until the visitor
// scrolls, taps or types.
const RESTORE_WINDOW_MS = 2500;
const LOAD_RESTORE_WINDOW_MS = 6000;
// Past that, until GROW_WINDOW_MS, it keeps waiting while the page is still
// too short to reach the spot (the sign-in check of a slow full load), and
// lines up again whenever the page's height changes: content drawn late above
// the spot (/chopshop's casting crew, its avatars answering after the window)
// moved what was on screen, and the browser's scroll anchoring followed it
// away from the spot, a crew's height down the gallery (2026-10-03).
const GROW_WINDOW_MS = 10_000;
const INTENTS = ['wheel', 'touchstart', 'pointerdown', 'keydown'] as const;
// How long after a click on a link to the page on screen its navigation
// still counts as that click's.
const SAME_URL_CLICK_MS = 1000;

// 'instant', not 'auto': html has scroll-behavior: smooth.
const jump = (top: number) => window.scrollTo({ top, left: 0, behavior: 'instant' });

// Kept under the entry's key, and under its URL (scroll-memory urlKey).
function keep(key: string, url: string, y: number, sig: string) {
  savePosition(key, y, sig);
  savePosition(url, y, sig);
}

// A full page load the browser made for Back / Forward (Back from Stripe
// Checkout) or a reload, as opposed to a typed or shared URL.
function loadedToRestore(): boolean {
  try {
    const nav = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined;
    return nav?.type === 'back_forward' || nav?.type === 'reload';
  } catch {
    return false;
  }
}

// A plain click on a link to the page on screen, without an #anchor (the
// navbar's or the footer's Pricing on /pricing).
function sameUrlLink(event: MouseEvent): boolean {
  if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return false;
  const target = event.target instanceof Element ? event.target : null;
  const link = target?.closest('a[href]');
  if (!(link instanceof HTMLAnchorElement) || link.hasAttribute('download')) return false;
  if (link.target && link.target !== '_self') return false;
  try {
    const url = new URL(link.href, window.location.href);
    const here = window.location;
    return url.origin === here.origin && url.pathname === here.pathname && url.search === here.search && !url.hash;
  } catch {
    return false;
  }
}

export function ScrollManager() {
  const location = useLocation();
  const navigationType = useNavigationType();
  const keyRef = useRef<string | null>(null);
  const urlRef = useRef<string | null>(null);
  const pathRef = useRef<string | null>(null);
  const searchRef = useRef<string | null>(null);
  const sameUrlClickAt = useRef(-Infinity);

  useEffect(() => {
    const h = window.history;
    const previous = 'scrollRestoration' in h ? h.scrollRestoration : null;
    if (previous) h.scrollRestoration = 'manual';
    // On every scroll, and on every click before it navigates: a browser can
    // hold a scroll event back past the navigation that followed it (WebKit
    // did, 2026-10-02), and Back then had nothing for the page left behind.
    const record = () => {
      if (keyRef.current && urlRef.current) keep(keyRef.current, urlRef.current, window.scrollY, pageSignature());
    };
    const click = (event: MouseEvent) => {
      record();
      if (sameUrlLink(event)) sameUrlClickAt.current = performance.now();
    };
    // Kept for a later full load when the page goes: closed, left for another
    // site (Stripe Checkout), put in the background (a phone may discard a
    // tab it put away without a pagehide), or frozen. With where it is right
    // now: the last scroll event may not have arrived yet.
    const leave = () => {
      record();
      persistPositions();
    };
    const hidden = () => {
      if (document.visibilityState === 'hidden') leave();
    };
    window.addEventListener('scroll', record, { passive: true });
    document.addEventListener('click', click, true);
    window.addEventListener('pagehide', leave);
    document.addEventListener('visibilitychange', hidden);
    document.addEventListener('freeze', leave);
    return () => {
      window.removeEventListener('scroll', record);
      document.removeEventListener('click', click, true);
      window.removeEventListener('pagehide', leave);
      document.removeEventListener('visibilitychange', hidden);
      document.removeEventListener('freeze', leave);
      if (previous && previous !== 'manual') h.scrollRestoration = previous;
    };
  }, []);

  // Layout effect: before paint, so the new page never shows at the old
  // offset, and before the pages' own effects (the landing's #hash scroll).
  useLayoutEffect(() => {
    // An entry the browser made without the router (a plain <a href="#id">).
    if (location.key === 'default') stampEntry();
    const key = entryKey(location);
    const url = urlKey(location);
    const firstPage = pathRef.current === null;
    const newPage = !firstPage && pathRef.current !== location.pathname;
    const sameUrl = !firstPage && !newPage && searchRef.current === location.search && !location.hash;
    const sameEntry = !firstPage && keyRef.current === key;
    keyRef.current = key;
    urlRef.current = url;
    pathRef.current = location.pathname;
    searchRef.current = location.search;

    // A pop that stays on the entry on screen: ChopBox's full screen pushes
    // an entry carrying the router's own key and leaves it with Back (or the
    // phone's Back does). Not a Back: the editor places the page itself, and
    // putting back the offset recorded while it was full screen fought that
    // ("Show me" never reached the gallery; review 2026-10-03).
    if (navigationType === 'POP' && sameEntry) return undefined;
    setRestoring(null);

    // An entry is recorded as it comes up, not at its first scroll: one the
    // visitor never scrolls must not keep the position of an earlier visit.
    if (navigationType !== 'POP') {
      if (newPage && !keepsScroll(location.state)) jump(0);
      // A link to the page on screen: the router stays on that page, so
      // nothing moved and the link seemed dead (review 2026-10-03). It goes
      // back to the top, as a link to another page would start there.
      const clicked = performance.now() - sameUrlClickAt.current < SAME_URL_CLICK_MS;
      sameUrlClickAt.current = -Infinity;
      if (sameUrl && clicked && !keepsScroll(location.state)) {
        window.scrollTo({ top: 0, left: 0, behavior: prefersReducedMotion() ? 'instant' : 'smooth' });
      }
      keep(key, url, window.scrollY, pageSignature());
      return undefined;
    }

    // Nothing to put back: an entry never recorded (a plain #anchor the
    // browser just followed), a page opened by a typed or shared URL, or a
    // page in another layout than when it was left (the landing reopened on
    // another compass stop: that Y would land in another section, and the
    // page's own arrival scroll, the credit packs' included, does better).
    // A reload that brought the entry back without its history state (the
    // reload of a crashed tab) stamped it a new key: its URL's copy stands in.
    const saved = savedPosition(key) ?? (firstPage && wasStamped(key) ? savedPosition(url) : undefined);
    const sig = pageSignature();
    if (!saved || (firstPage && !loadedToRestore()) || saved.sig !== sig) {
      keep(key, url, window.scrollY, sig);
      return undefined;
    }
    const target = saved.y;
    setRestoring(key);

    let alive = true;
    let frame = 0;
    const start = performance.now();
    const until = start + (firstPage ? LOAD_RESTORE_WINDOW_MS : RESTORE_WINDOW_MS);
    const grownBy = start + GROW_WINDOW_MS;
    const root = document.documentElement;
    const reachable = () => root.scrollHeight - window.innerHeight >= target - 1;
    let height = root.scrollHeight;
    const stop = () => {
      alive = false;
      cancelAnimationFrame(frame);
      INTENTS.forEach((type) => window.removeEventListener(type, stop));
    };
    const line = () => {
      if (!alive) return;
      const now = performance.now();
      const grew = root.scrollHeight !== height;
      height = root.scrollHeight;
      // Every frame in the window; past it, only while the spot is out of
      // reach or as the page's height changes. A scroll the page makes itself
      // after the window (nothing grew) is left alone.
      if ((now < until || grew || !reachable()) && Math.abs(window.scrollY - target) > 1) jump(target);
      if (now < grownBy) {
        frame = requestAnimationFrame(line);
        return;
      }
      // Out of time short of the spot (the page never grew back that tall):
      // the page's own arrival scroll may run after all, the credit packs'
      // once they load (review 2026-10-03). Reached, or the visitor took
      // over (stop, above): it stays a restore, and nothing pulls the page
      // away from there.
      if (Math.abs(window.scrollY - target) > 1) setRestoring(null);
      stop();
    };
    INTENTS.forEach((type) => window.addEventListener(type, stop, { passive: true }));
    line();
    return stop;
  }, [location, navigationType]);

  return null;
}
